import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import './Navbar.css'

// Fonction pour décoder le token JWT
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Erreur lors du décodage du token JWT:', error)
    return null
  }
}

const Navbar: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user, logout, token } = useAuthStore()
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Extraire les informations utilisateur depuis le token JWT
  const userInfo = React.useMemo(() => {
    if (!token) return { email: 'Email non disponible', username: 'Utilisateur' }
    
    const payload = decodeJWT(token)
    if (!payload) return { email: 'Email non disponible', username: 'Utilisateur' }
    
    return {
      email: payload.email || user?.email || 'Email non disponible',
      username: payload.username || user?.username || 'Utilisateur'
    }
  }, [token, user?.email, user?.username])

  const handleLogout = () => {
    logout()
  }

  const handleProfileDropdownToggle = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen)
  }

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : ''
  }

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-brand">
          <h1>EdenPlaisir.com</h1>
        </Link>

        <div className="navbar-menu">
          <Link 
            to="/" 
            className={`navbar-item ${isActive('/')}`}
          >
            Accueil
          </Link>

          <Link 
            to="/categories" 
            className={`navbar-item ${isActive('/categories')}`}
          >
            Découvrir
          </Link>

          {isAuthenticated && (
            <>
              {user?.roles?.includes('ROLE_MODERATOR') && (
                <Link 
                  to="/moderation" 
                  className={`navbar-item ${isActive('/moderation')}`}
                >
                  Modération
                </Link>
              )}

              {user?.roles?.includes('ROLE_ADMIN') && (
                <Link 
                  to="/admin" 
                  className={`navbar-item ${isActive('/admin')}`}
                >
                  Administration
                </Link>
              )}
            </>
          )}
        </div>

        <div className="navbar-auth">
          {isAuthenticated ? (
            <div className="user-menu">
              <button 
                className="btn-subscribe"
                onClick={() => navigate('/subscriptions')}
              >
                Souscrire un abonnement
              </button>
              
              <div className="profile-dropdown" ref={dropdownRef}>
                <button 
                  className="profile-button"
                  onClick={handleProfileDropdownToggle}
                >
                  <svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <path 
                      d="M20.59 22C20.59 18.13 16.74 15 12 15C7.26 15 3.41 18.13 3.41 22" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                
                {isProfileDropdownOpen && (
                  <div className="profile-dropdown-menu">
                    <div className="profile-info">
                      <div className="profile-username">
                        {userInfo.username}
                      </div>
                      <div className="profile-email">
                        {userInfo.email}
                      </div>
                    </div>
                    <div className="dropdown-divider"></div>
                    <Link 
                      to="/subscriptions" 
                      className="dropdown-item"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      Gérer mon abonnement
                    </Link>
                    <Link 
                      to="/invoices" 
                      className="dropdown-item"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      Mes factures
                    </Link>
                    <button 
                      onClick={() => {
                        handleLogout()
                        setIsProfileDropdownOpen(false)
                      }}
                      className="dropdown-item dropdown-logout"
                    >
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="auth-links">
              <Link 
                to="/login" 
                className={`navbar-item ${isActive('/login')}`}
              >
                Connexion
              </Link>
              <Link 
                to="/register" 
                className={`navbar-item ${isActive('/register')}`}
              >
                Inscription
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}

export default Navbar
